/* ──────────────────────────────────────────────────────────────────────────
 * CostTrackerDesk — Cost & Payment Tracker for MSME IPOs (LIVE).
 *
 * Faithful port of the supplied `cost_payment_tracker_v2_msme.html`, wired to
 * the live /api/cost-tracker backend. Every table/row is fully editable
 * (party/expert names, description, amount, who-paid, status), with add/delete,
 * a "Sync advisors" pull from the Advisor Marketplace, and Export to Excel / PDF
 * / Share. New users start with an EMPTY tracker (six standard channels, no
 * line items) and fill in the parties they onboard.
 * Styles scoped under `.ct-desk` in CostTracker.css.
 * ────────────────────────────────────────────────────────────────────────── */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { logJourney } from "@/lib/journey";
import "./CostTracker.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTS = [
  { v: "paid", l: "Paid" },
  { v: "due", l: "To be paid" },
  { v: "upcoming", l: "Upcoming" },
  { v: "recurring", l: "Recurring" },
];
const STATUS_LABEL = { paid: "Paid", due: "To be paid", upcoming: "Upcoming", recurring: "Recurring" };
const stClass = (s) => (s === "paid" ? "li-paid" : s === "recurring" ? "li-up" : s === "upcoming" ? "li-up" : "li-due");
const catTagClass = (t) => ({ paid: "tg-paid", part: "tg-part", due: "tg-due", up: "tg-up", empty: "tg-up" }[t] || "tg-up");
const catTagLabel = (t) => ({ paid: "Paid", part: "Part-paid", due: "Due", up: "Upcoming", empty: "Empty" }[t] || "—");

const fmtL = (n) => {
  const v = Number(n || 0);
  return `₹${v % 1 === 0 ? v.toFixed(0) : v.toFixed(2)} L`;
};
const initialsOf = (s) => (s || "—").trim().charAt(0).toUpperCase() || "—";

const EMPTY_ITEM = { category_id: "", name: "", desc: "", amount: "", paid_by: "", status: "due" };

export const CostTrackerDesk = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({}); // category accordion open-state
  const [modal, setModal] = useState(null); // { mode:'add'|'edit', item }
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [editIssue, setEditIssue] = useState(false);
  const [issueVal, setIssueVal] = useState("");

  const load = useCallback(() => {
    fetch(`${API_URL}/api/cost-tracker`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        setData(d);
        if (d) setIssueVal(String(d.issue_size_cr || ""));
        // open the first category by default the first time
        setOpen((prev) => (Object.keys(prev).length ? prev : { [d?.categories?.[0]?.id]: true }));
      })
      .catch(() => {});
  }, []);

  useEffect(() => { load(); logJourney("Cost Tracker", "Viewed", "Opened Cost & Payment Tracker"); }, [load]);

  const s = data?.summary || {};
  const totalForBar = (s.spent || 0) + (s.due || 0) + (s.upcoming || 0) || 1;
  const pct = (n) => `${Math.round(((n || 0) / totalForBar) * 100)}%`;

  const toggle = (id) => setOpen((p) => ({ ...p, [id]: !p[id] }));

  const openAdd = (catId) => setModal({ mode: "add", item: { ...EMPTY_ITEM, category_id: catId || data?.categories?.[0]?.id || "" } });
  const openEdit = (catId, it) => setModal({ mode: "edit", item: { ...it, category_id: catId, amount: String(it.amount ?? "") } });

  const saveItem = async () => {
    const it = modal.item;
    if (!it.name.trim()) { toast.error("Enter the party / item name."); return; }
    setSaving(true);
    try {
      const body = {
        name: it.name, desc: it.desc, amount: parseFloat(it.amount) || 0,
        paid_by: it.paid_by, status: it.status,
      };
      let res;
      if (modal.mode === "add") {
        res = await fetch(`${API_URL}/api/cost-tracker/item`, {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ category_id: it.category_id, ...body }),
        });
      } else {
        res = await fetch(`${API_URL}/api/cost-tracker/item/${it.id}`, {
          method: "PATCH", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
      }
      const d = await res.json();
      if (!res.ok) throw new Error(d?.detail || "Save failed");
      setData(d);
      setModal(null);
      toast.success(modal.mode === "add" ? "Cost added." : "Cost updated.");
    } catch (e) {
      toast.error(e.message || "Could not save.");
    }
    setSaving(false);
  };

  const deleteItem = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/cost-tracker/item/${id}`, { method: "DELETE", credentials: "include" });
      const d = await res.json();
      if (!res.ok) throw new Error();
      setData(d);
      toast.success("Cost removed.");
    } catch { toast.error("Could not delete."); }
  };

  const syncAdvisors = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API_URL}/api/cost-tracker/sync-advisors`, { method: "POST", credentials: "include" });
      const d = await res.json();
      setData(d);
      toast.success(d.added > 0 ? `Pulled ${d.added} engaged advisor(s) from the Marketplace.` : "No new engaged advisors to add.");
    } catch { toast.error("Sync failed."); }
    setSyncing(false);
  };

  const doExport = async (fmt) => {
    setExporting(true);
    try {
      const res = await fetch(`${API_URL}/api/cost-tracker/export?format=${fmt}`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Cost_Payment_Tracker.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success(`Exported ${fmt.toUpperCase()}.`);
    } catch { toast.error("Export failed."); }
    setExporting(false);
  };

  const doShare = async () => {
    const summary = `IPO Cost & Payment Tracker${data?.company ? ` — ${data.company}` : ""}\n` +
      `Total budget: ${fmtL(s.totalBudget)} · Spent: ${fmtL(s.spent)} · Due/upcoming: ${fmtL((s.due || 0) + (s.upcoming || 0))}\n` +
      `Owed to company (reimbursement): ${fmtL(s.owedToCompany)} · ${s.lineItems || 0} line items`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "IPO Cost & Payment Tracker", text: summary });
        logJourney("Cost Tracker", "Shared", "Shared the cost summary");
      } else {
        await navigator.clipboard.writeText(summary);
        toast.success("Cost summary copied to clipboard — paste it anywhere to share.");
        logJourney("Cost Tracker", "Shared", "Copied the cost summary to clipboard");
      }
    } catch { /* user cancelled share */ }
  };

  const saveIssue = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cost-tracker/settings`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ issue_size_cr: parseFloat(issueVal) || 0 }),
      });
      const d = await res.json();
      setData(d);
      setEditIssue(false);
    } catch { toast.error("Could not update issue size."); }
  };

  if (!data) return <div className="ct-desk"><div className="ct-loading" data-testid="ct-loading">Loading your Cost &amp; Payment Tracker…</div></div>;

  const cats = data.categories || [];
  const setItemField = (k, v) => setModal((m) => ({ ...m, item: { ...m.item, [k]: v } }));

  return (
    <div className="ct-desk" data-testid="cost-tracker-desk">
      <div className="ct">
        {/* Top bar */}
        <div className="ct-bar">
          <div className="ct-logo"><i className="ti ti-wallet" style={{ color: "#fff", fontSize: 17 }} aria-hidden="true" /></div>
          <div>
            <div className="ct-title">Cost &amp; Payment Tracker</div>
            <div className="ct-sub-t">Every IPO cost, across every channel</div>
          </div>
          <div className="ct-co">
            <button className="ct-exp" onClick={syncAdvisors} disabled={syncing} data-testid="ct-sync-advisors">
              <i className={`ti ti-refresh ${syncing ? "spin" : ""}`} style={{ fontSize: 12 }} aria-hidden="true" /> {syncing ? "Syncing…" : "Sync advisors"}
            </button>
            <div className="ct-export-wrap">
              <button className="ct-exp" disabled={exporting} data-testid="ct-export-btn">
                <i className="ti ti-download" style={{ fontSize: 12 }} aria-hidden="true" /> Export ledger <i className="ti ti-chevron-down" style={{ fontSize: 11 }} />
              </button>
              <div className="ct-export-menu">
                <button onClick={() => doExport("xlsx")} data-testid="ct-export-excel"><i className="ti ti-file-spreadsheet" /> Excel (.xlsx)</button>
                <button onClick={() => doExport("pdf")} data-testid="ct-export-pdf"><i className="ti ti-file-type-pdf" /> PDF</button>
                <button onClick={doShare} data-testid="ct-share"><i className="ti ti-share" /> Share summary</button>
              </div>
            </div>
            <button className="ct-add" onClick={() => openAdd()} data-testid="ct-add-cost">+ Add cost</button>
          </div>
        </div>

        {/* Hero budget summary */}
        <div className="bhero">
          <div className="bh-top">
            <div className="bh-block"><div className="l">Total IPO budget</div><div className="v" data-testid="ct-total">{fmtL(s.totalBudget)}</div></div>
            <div className="bh-block"><div className="l">Spent</div><div className="v spent" data-testid="ct-spent">{fmtL(s.spent)}</div></div>
            <div className="bh-block"><div className="l">Due / upcoming</div><div className="v left">{fmtL((s.due || 0) + (s.upcoming || 0))}</div></div>
            <div className="bh-block"><div className="l">Owed to company*</div><div className="v owed" data-testid="ct-owed">{fmtL(s.owedToCompany)}</div></div>
            <div className="bh-feed">
              <div className="ff"><i className="ti ti-arrow-up-right" aria-hidden="true" /> Syncs to IPO Navigator</div>
              <div className="ff"><i className="ti ti-briefcase" aria-hidden="true" /> Fees from Advisor Marketplace</div>
              <div className="ff"><i className="ti ti-coins" aria-hidden="true" /> Funded via Funding Desk</div>
            </div>
          </div>
          <div className="bbar">
            <div style={{ width: pct(s.spent), background: "#6C4BF4" }} />
            <div style={{ width: pct(s.due), background: "#a86711" }} />
            <div style={{ width: pct(s.upcoming), background: "#2a8a5a" }} />
          </div>
          <div className="blegend">
            <span className="bl"><span className="dot" style={{ background: "#6C4BF4" }} /> Paid {fmtL(s.spent)}</span>
            <span className="bl"><span className="dot" style={{ background: "#a86711" }} /> Due {fmtL(s.due)}</span>
            <span className="bl"><span className="dot" style={{ background: "#2a8a5a" }} /> Budgeted {fmtL(s.upcoming)}</span>
            <span className="bl" style={{ color: "#8a8a94" }}>*Paid by promoter/group, to be reimbursed by company</span>
          </div>
        </div>

        {/* KPIs */}
        <div className="kpis">
          <div className="kk"><div className="kk-v p">{s.categoriesCount || 0}</div><div className="kk-l">Cost categories</div></div>
          <div className="kk"><div className="kk-v">{s.lineItems || 0}</div><div className="kk-l">Line items</div></div>
          <div className="kk"><div className="kk-v a">{fmtL(s.due)}</div><div className="kk-l">Due (to be paid)</div></div>
          <div className="kk"><div className="kk-v o">{fmtL(s.owedToCompany)}</div><div className="kk-l">Reimbursement pending</div></div>
          <div className="kk" onClick={() => setEditIssue(true)} style={{ cursor: "pointer" }} title="Click to set your issue size" data-testid="ct-issue-kpi">
            {editIssue ? (
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <input className="ct-issue-input" autoFocus value={issueVal} onChange={(e) => setIssueVal(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && saveIssue()} placeholder="₹ Cr" data-testid="ct-issue-input" />
                <button className="ct-issue-save" onClick={saveIssue} data-testid="ct-issue-save"><i className="ti ti-check" /></button>
              </div>
            ) : (
              <>
                <div className="kk-v g">{s.pctOfIssue ? `${s.pctOfIssue}%` : "—"}</div>
                <div className="kk-l">{data.issue_size_cr ? `of ₹${data.issue_size_cr} Cr issue size` : "Set issue size →"}</div>
              </>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="body">
          <div className="sec-lbl">
            <i className="ti ti-layout-list" aria-hidden="true" /> Costs by channel
            <span className="src">↻ advisor fees synced from Advisor Marketplace</span>
          </div>

          {cats.map((c) => {
            const ct = s.categoryTotals?.[c.id] || { total: 0, pct: 0, tag: "empty", count: 0 };
            const isOpen = !!open[c.id];
            return (
              <div className={`cat ${isOpen ? "open" : ""}`} key={c.id} data-testid={`ct-cat-${c.id}`}>
                <div className="cat-head" onClick={() => toggle(c.id)} data-testid={`ct-cat-head-${c.id}`}>
                  <div className="cat-ic" style={{ background: c.bg, color: c.fg }}><i className={`ti ${c.icon}`} aria-hidden="true" /></div>
                  <div><div className="cat-n">{c.name}</div><div className="cat-sub">{c.sub}</div></div>
                  <div className="cat-bar"><i style={{ width: `${ct.pct}%` }} /></div>
                  <div className="cat-amt">{fmtL(ct.total)} <span className="s">{ct.count} item{ct.count === 1 ? "" : "s"}</span></div>
                  <div className={`cat-tag ${catTagClass(ct.tag)}`}>{catTagLabel(ct.tag)}</div>
                  <i className="ti ti-chevron-down chev" aria-hidden="true" />
                </div>
                <div className="cat-body">
                  {(c.items || []).length === 0 ? (
                    <div className="li-empty">No costs added yet. <button className="li-empty-add" onClick={() => openAdd(c.id)} data-testid={`ct-empty-add-${c.id}`}>+ Add a party / cost</button></div>
                  ) : (
                    (c.items || []).map((it) => (
                      <div className="li" key={it.id} data-testid={`ct-item-${it.id}`}>
                        <div><div className="li-n">{it.name}</div><div className="li-d">{it.desc}</div></div>
                        <div className="li-amt">{fmtL(it.amount)}</div>
                        <div className="li-by">
                          <span className="av" style={{ background: it.paid_by ? "#e7f6ec" : "#f1f0f6", color: it.paid_by ? "#16794a" : "#55555f" }}>{it.paid_by ? initialsOf(it.paid_by) : "—"}</span>
                          {it.paid_by ? `Paid by ${it.paid_by}` : "Pending"}
                        </div>
                        <div className={`li-st ${stClass(it.status)}`}>{STATUS_LABEL[it.status] || it.status}</div>
                        <div className="li-actions">
                          <button onClick={() => openEdit(c.id, it)} title="Edit" data-testid={`ct-edit-${it.id}`}><i className="ti ti-pencil" /></button>
                          <button onClick={() => deleteItem(it.id)} title="Delete" data-testid={`ct-del-${it.id}`}><i className="ti ti-trash" /></button>
                        </div>
                      </div>
                    ))
                  )}
                  {(c.items || []).length > 0 && (
                    <div className="li-addrow"><button className="li-empty-add" onClick={() => openAdd(c.id)} data-testid={`ct-add-${c.id}`}>+ Add another cost to this channel</button></div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Reimbursements + funding cards */}
          <div className="cols">
            <div className="card">
              <div className="card-t"><i className="ti ti-receipt-refund" style={{ color: "#6C4BF4" }} aria-hidden="true" /> Reimbursements &amp; who paid</div>
              {(s.reimbursements || []).length === 0 ? (
                <div className="reimb-empty">No reimbursable payments yet. As you mark costs <b>Paid</b> and note who paid them, the breakdown appears here.</div>
              ) : (
                (s.reimbursements || []).map((r, i) => (
                  <div className="reimb" key={i} data-testid={`ct-reimb-${i}`}>
                    <div className="reimb-av" style={{ background: "#e7f6ec", color: "#16794a" }}>{initialsOf(r.payer)}</div>
                    <div className="reimb-mid"><div className="reimb-h">Paid by {r.payer}</div><div className="reimb-d">{r.count} item{r.count === 1 ? "" : "s"} paid upfront, awaiting reimbursement</div></div>
                    <div className="reimb-v" style={{ color: "#d97706" }}>{fmtL(r.amount)}</div>
                  </div>
                ))
              )}
              <div className="runway"><b>Why this matters:</b> IPO bills get paid by whoever has cash on hand — promoter, group company, or the issuer. Tracking <b>who paid</b> keeps reimbursements clean and feeds the related-party + "objects of issue" disclosures in your DRHP.</div>
            </div>
            <div className="card">
              <div className="card-t"><i className="ti ti-coins" style={{ color: "#6C4BF4" }} aria-hidden="true" /> How it's funded <span className="src">↻ Funding Desk</span></div>
              <div className="fund-row"><div className="fund-ic"><i className="ti ti-cash" aria-hidden="true" /></div><div className="fund-mid"><div className="fund-h">Total spent so far</div><div className="fund-d">Across all paid line items</div></div><div className="fund-v">{fmtL(s.spent)}</div></div>
              <div className="fund-row"><div className="fund-ic"><i className="ti ti-building-bank" aria-hidden="true" /></div><div className="fund-mid"><div className="fund-h">Due in the near term</div><div className="fund-d">Invoices marked "to be paid"</div></div><div className="fund-v">{fmtL(s.due)}</div></div>
              <div className="runway"><b>Runway:</b> {fmtL(s.due)} currently due. SME IPO costs typically run 7–10% of issue size — {s.pctOfIssue ? `you're tracking at ~${s.pctOfIssue}%.` : "set your issue size to see where you stand."} Plan your next funding draw via the Funding Desk before the BRLM filing tranche.</div>
            </div>
          </div>
        </div>
      </div>

      {/* Add / Edit modal */}
      {modal && (
        <div className="ct-modal-overlay" onClick={() => !saving && setModal(null)}>
          <div className="ct-modal" onClick={(e) => e.stopPropagation()} data-testid="ct-modal">
            <div className="ct-modal-h">{modal.mode === "add" ? "Add a cost / party" : "Edit cost"}</div>
            <div className="ct-field">
              <label>Cost channel</label>
              <select value={modal.item.category_id} onChange={(e) => setItemField("category_id", e.target.value)} disabled={modal.mode === "edit"} data-testid="ct-modal-category">
                {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="ct-field">
              <label>Party / expert / item name</label>
              <input value={modal.item.name} onChange={(e) => setItemField("name", e.target.value)} placeholder="e.g. Lexguroo Advisory LLP" data-testid="ct-modal-name" />
            </div>
            <div className="ct-field">
              <label>Description</label>
              <input value={modal.item.desc} onChange={(e) => setItemField("desc", e.target.value)} placeholder="e.g. Legal — due diligence & drafting" data-testid="ct-modal-desc" />
            </div>
            <div className="ct-field-row">
              <div className="ct-field">
                <label>Amount (₹ Lakhs)</label>
                <input type="number" min="0" step="0.01" value={modal.item.amount} onChange={(e) => setItemField("amount", e.target.value)} placeholder="0.00" data-testid="ct-modal-amount" />
              </div>
              <div className="ct-field">
                <label>Status</label>
                <select value={modal.item.status} onChange={(e) => setItemField("status", e.target.value)} data-testid="ct-modal-status">
                  {STATUS_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>
            </div>
            <div className="ct-field">
              <label>Paid by (party / leave blank if pending)</label>
              <input value={modal.item.paid_by} onChange={(e) => setItemField("paid_by", e.target.value)} placeholder="e.g. Novavente (promoter), Company, Milestone-linked…" data-testid="ct-modal-paidby" />
            </div>
            <div className="ct-modal-foot">
              <button className="ct-modal-cancel" onClick={() => setModal(null)} disabled={saving} data-testid="ct-modal-cancel">Cancel</button>
              <button className="ct-modal-save" onClick={saveItem} disabled={saving} data-testid="ct-modal-save">{saving ? "Saving…" : modal.mode === "add" ? "Add cost" : "Save changes"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CostTrackerDesk;
